import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { coreAPI, ConnectionCompany } from '../../lib/api';
import { AlertCircle, Loader2 } from 'lucide-react';
import { NewConcessionForm } from './useConcessions';

interface CreateConcessionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: NewConcessionForm;
  onFormChange: (form: NewConcessionForm) => void;
  onSubmit: () => void;
  loading: boolean;
  /**
   * Message from a create that failed. Rendered in place so the dialog can
   * stay open with the user's input intact instead of the page swapping
   * itself for a full-screen error and taking the half-typed form with it.
   */
  error?: string | null;
}

export function CreateConcessionDialog({
  open,
  onOpenChange,
  form,
  onFormChange,
  onSubmit,
  loading,
  error,
}: CreateConcessionDialogProps) {
  // The customer the deviation is raised against. The backend requires it and
  // the supplier is the only one who knows which customer this is for, so it
  // has to be asked here — a PO-line concession reads it off the line instead.
  const [customers, setCustomers] = useState<ConnectionCompany[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCustomersLoading(true);
    coreAPI.getMyConnections({ limit: 2000 })
      .then(res => {
        if (cancelled) return;
        setCustomers(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => { if (!cancelled) setCustomers([]); })
      .finally(() => { if (!cancelled) setCustomersLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  const isValid = form.customer && form.title && form.part_number && form.quantity_affected && form.description && form.reason;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Concession Request</DialogTitle>
          <DialogDescription>
            Submit a deviation request for customer approval.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
          >
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="customer">Customer *</Label>
            <Select
              value={form.customer}
              onValueChange={(value) => onFormChange({ ...form, customer: value })}
              disabled={customersLoading}
            >
              <SelectTrigger id="customer">
                <SelectValue placeholder={customersLoading ? 'Loading customers...' : 'Select customer'} />
              </SelectTrigger>
              <SelectContent>
                {customers.map((company) => (
                  <SelectItem key={company.id} value={String(company.id)}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!customersLoading && customers.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No customers are linked to your account yet — raise the concession
                from the purchase order line instead.
              </p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={form.title}
              onChange={(e) => onFormChange({ ...form, title: e.target.value })}
              placeholder="Brief title of the deviation"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="part_number">Part Number *</Label>
              <Input
                id="part_number"
                value={form.part_number}
                onChange={(e) => onFormChange({ ...form, part_number: e.target.value })}
                placeholder="Part number"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="quantity_affected">Quantity Affected *</Label>
              <Input
                id="quantity_affected"
                type="number"
                step="any"
                min="0"
                value={form.quantity_affected}
                onChange={(e) => onFormChange({ ...form, quantity_affected: e.target.value })}
                placeholder="Number of units"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="deviation_type">Deviation Type *</Label>
              <Select
                value={form.deviation_type}
                onValueChange={(value) => onFormChange({ ...form, deviation_type: value as NewConcessionForm['deviation_type'] })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dimensional">Dimensional</SelectItem>
                  <SelectItem value="cosmetic">Cosmetic</SelectItem>
                  <SelectItem value="material">Material</SelectItem>
                  <SelectItem value="process">Process</SelectItem>
                  <SelectItem value="documentation">Documentation</SelectItem>
                  <SelectItem value="packaging">Packaging</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="risk_level">Risk Level *</Label>
              <Select
                value={form.risk_level}
                onValueChange={(value) => onFormChange({ ...form, risk_level: value as NewConcessionForm['risk_level'] })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select risk" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              value={form.description}
              onChange={(e) => onFormChange({ ...form, description: e.target.value })}
              placeholder="Detailed description of the deviation"
              rows={3}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="reason">Reason for Request *</Label>
            <Textarea
              id="reason"
              value={form.reason}
              onChange={(e) => onFormChange({ ...form, reason: e.target.value })}
              placeholder="Why is this concession being requested?"
              rows={3}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="proposed_disposition">Proposed Disposition</Label>
            <Textarea
              id="proposed_disposition"
              value={form.proposed_disposition}
              onChange={(e) => onFormChange({ ...form, proposed_disposition: e.target.value })}
              placeholder="Proposed action (use as-is, rework, etc.)"
              rows={2}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="risk_justification">Risk Justification</Label>
            <Textarea
              id="risk_justification"
              value={form.risk_justification}
              onChange={(e) => onFormChange({ ...form, risk_justification: e.target.value })}
              placeholder="Justification for the assigned risk level"
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={loading || !isValid}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Concession'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
